import { css } from "@emotion/react";
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  UIEvent as ReactUIEvent
} from "react";
import { CloseXRed, spaik, WhatsAppCheck, Smile } from "../../assets";
import { AuthContextType, useAuth } from "../../auth/auth-provider";
import { Tooltip } from "primereact/tooltip";
import { format, isToday, isAfter } from "date-fns";
import { iFile } from "../use-omnichannel";
import { AudioPlayer } from "./AudioPreview";
import { VideoPreview } from "./VideoPreview";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  addUrls,
  removeUrls,
  setEmojiModalOpen
} from "../../redux/slices/flowInteractions";
import {
  DropdownDeleteMessage,
  ModalEditMessage,
  ForwardMessageModal,
  ImgIcon,
  ParentId
} from "./styles";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import OmnichannelService from "../../services/omnichannelService";
import { useToast } from "../../components";
import ColorSvg from "../../components/ColorSvg";
import { EmojiModal } from "../../components/Interactions/components/interacaoRobo/EmojiModal";
import useOmnichannel, { EditMessage } from "../use-omnichannel";
import {
  setRefreshAllDialogs,
  setReplyMessage,
  setModalEdit
} from "../../redux/slices/omnichannel";
import { Message as IMessage } from "../use-omnichannel";
import useContacts from "../../components/ContactsList/use-contacts";
import Avatar from "../avatar";
import { ImageViewer } from "./image-viewer";

const getMaskedDate = (data: Date): string => {
  const messageDate = new Date(data);
  const currentDate = new Date();

  if (isToday(messageDate)) {
    return format(messageDate, "HH:mm");
  } else if (isAfter(currentDate, messageDate)) {
    return format(messageDate, "dd/MM/yyyy (HH:mm)");
  }

  return "";
};

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

export interface StatusMessageProps {
  message: string;
}

export const StatusMessage = ({ message }: StatusMessageProps) => (
  <div
    css={css`
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;
      margin-bottom: 10px;
      margin-top: 10px;
    `}
  >
    <div
      css={css`
        background-color: #c4e2ff;
        border-radius: 800px;
        display: flex;
        font-size: 12px;
        padding: 5px 10px;
        z-index: 1;
      `}
    >
      {message}
    </div>

    <div
      css={css`
        border-bottom: 1px solid #e5e5e5;
        width: 100%;
        position: absolute;
        top: 50%;
      `}
    ></div>
  </div>
);

const URL_REGEX =
  /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/iu;

type QuotedMessage = {
  parentId: number;
  message: string;
  mime?: string;
};

export interface MessageProps {
  id: number;
  message: string;
  dialogId: number;
  isHistoricalMessage?: boolean;
  canQuoteMessage?: boolean;
  robotName: string;
  chatName: string;
  humanName?: string;
  reply: boolean;
  isHumanResponder: boolean;
  files: iFile[];
  date: Date;
  updatedAt?: Date;
  status?: string;
  forwardedMessage?: boolean;
  quotedMessage?: QuotedMessage;
  sender: IMessage["sender"];
  deletedParentIds: Set<number>;
  onMessageDeleted: (id: number) => void;
  sendState?: "sending" | "failed";
  errorText?: string;
  onRetry?: () => void;
  onForwardMessage: (
    messageId: number,
    text: string,
    contactIds: number[],
    sourceDialogId: number
  ) => void;
}

type ForwardContact = {
  id: number;
  name: string | null;
  phone: string | null;
  avatarUrl?: string;
};

const FORWARD_CONTACTS_LIMIT = 20;

const FileImagePreview = ({ file }: { file: iFile }) => {
  const { mime } = file;
  const newUrls = useAppSelector((state) => state.flowInteractions.newUrl);
  const dispatch = useAppDispatch();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const imageUrls = useMemo(
    () => Array.from(new Set(newUrls.filter(Boolean))),
    [newUrls]
  );

  useEffect(() => {
    dispatch(addUrls([file.url]));

    return () => {
      dispatch(removeUrls());
    };
  }, [dispatch, file.url]);

  const handleImageClick = (url: string) => {
    const index = imageUrls.indexOf(url);
    if (index !== -1) {
      setActiveIndex(index);
      setIsViewerOpen(true);
    }
  };

  if (mime.startsWith("image")) {
    return (
      <div
        css={css`
          width: 100%;
          display: flex;
          justify-content: center;
        `}
      >
        <img
          src={file.url}
          height={200}
          css={css`
            cursor: pointer;
            max-width: 100%;
          `}
          onClick={() => handleImageClick(file.url)}
        />
        <ImageViewer
          images={imageUrls}
          initialIndex={activeIndex}
          open={isViewerOpen}
          onClose={() => setIsViewerOpen(false)}
        />
      </div>
    );
  }

  return null;
};

const FileLinkURL = ({
  filename,
  file
}: {
  filename: string;
  message: string;
  file?: iFile;
}) => {
  const normalizedFilename = filename?.trim();
  const normalizedUrl = file?.url?.trim() || "";
  const fallbackUrlLabel = (() => {
    if (!normalizedUrl) return "";

    try {
      const parsedUrl = new URL(normalizedUrl);
      const lastPathSegment = parsedUrl.pathname
        .split("/")
        .filter(Boolean)
        .pop();

      return lastPathSegment || normalizedUrl.replace(/^https?:\/\//i, "");
    } catch {
      const sanitizedUrl = normalizedUrl.replace(/^https?:\/\//i, "");
      return sanitizedUrl.split("/").filter(Boolean).pop() || sanitizedUrl;
    }
  })();
  const linkLabel = normalizedFilename || fallbackUrlLabel || "Arquivo";

  return (
    <a
      href={normalizedUrl || undefined}
      target="_blank"
      rel="noreferrer"
      style={{ wordBreak: "break-all" }}
    >
      {linkLabel}
    </a>
  );
};

export const Message = ({
  id,
  message,
  dialogId,
  isHistoricalMessage = false,
  canQuoteMessage = true,
  isHumanResponder,
  files,
  robotName,
  chatName,
  humanName,
  reply,
  date,
  updatedAt,
  status,
  forwardedMessage = false,
  quotedMessage,
  sender,
  deletedParentIds,
  onMessageDeleted,
  sendState,
  onRetry,
  onForwardMessage
}: MessageProps) => {
  let messageContent;

  const { user, hasAnyPermission } = useAuth() as AuthContextType;
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalForward, setShowModalForward] = useState(false);
  const [forwardSearch, setForwardSearch] = useState("");
  const [forwardContacts, setForwardContacts] = useState<ForwardContact[]>([]);
  const [forwardContactsLoading, setForwardContactsLoading] = useState(false);
  const [forwardContactsOffset, setForwardContactsOffset] = useState(0);
  const [forwardContactsHasMore, setForwardContactsHasMore] = useState(true);
  const [selectedForwardContacts, setSelectedForwardContacts] = useState<
    number[]
  >([]);
  const debouncedForwardSearch = useDebounce(forwardSearch, 600);
  const [editedText, setEditedText] = useState(message);
  const { deleteMessages } = OmnichannelService();
  const { ListContactsOmnichannel } = useContacts();
  const toast = useToast();
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const editedTextRef = useRef<HTMLTextAreaElement | null>(null);
  const forwardContactsLoadingRef = useRef(false);
  const whatsappQrCode = useAppSelector(
    (state) => state.omnichannel.whatsappQrCode
  );
  const selectedWorkspacesIds = useAppSelector(
    (state) => state.workspaces.selectedWorkspaces
  );
  const emoji = useAppSelector(
    (state) => state.flowInteractions.valueTextEmoji
  );
  const emojiModalOpen = useAppSelector(
    (state) => state.flowInteractions.emojiModal
  );
  const [renderedMessage, setRenderedMessage] = useState(message);
  const [renderedUpdatedAt, setRenderedUpdatedAt] = useState<Date | undefined>(
    updatedAt
  );
  const quotedParentId = quotedMessage?.parentId ?? 0;
  const quotedContent = (quotedMessage?.message ?? "").trim();
  const dispatch = useAppDispatch();
  const { editMessage } = useOmnichannel();
  const [textMessageDeleted, setTextMessageDeleted] = useState(false);
  const [deletedMessage, setDeletedMessage] = useState(false);
  const messageSenderName = humanName?.trim() || user.name;
  const forwardWorkspaces = useMemo(() => {
    if (selectedWorkspacesIds.length > 0) {
      return selectedWorkspacesIds;
    }

    return user.workspaces?.workspaces?.map((workspace) => workspace.id) ?? [];
  }, [selectedWorkspacesIds, user.workspaces?.workspaces]);

  const hasFiles = Array.isArray(files) && files.length > 0;
  const hasText = typeof message === "string" && message.trim() !== "";
  if (!hasFiles && !hasText) return null;

  const isClient = sender === "CLIENT";
  const isHuman = sender === "HUMAN";
  const messageTooltipClass = `message-sender-tooltip-${id}`;
  const tooltipSenderName = isClient
    ? chatName
    : isHuman
    ? messageSenderName
    : robotName;
  const isTemporaryFailed = sendState === "failed";
  const isForwardedMessage = Boolean(forwardedMessage);
  const isPendingMessageStatus =
    whatsappQrCode && !isClient && status === "PENDING";
  const isServerAckMessageStatus =
    whatsappQrCode && !isClient && status === "SERVER_ACK";
  const isDeliveryAckMessageStatus =
    whatsappQrCode && !isClient && status === "DELIVERY_ACK";
  const isReadMessageStatus = whatsappQrCode && !isClient && status === "READ";
  const isPlayedMessageStatus =
    whatsappQrCode && !isClient && status === "PLAYED";
  const isErrorMessageStatus =
    whatsappQrCode && !isClient && status === "ERROR";
  const canReplyToMessage =
    whatsappQrCode && canQuoteMessage && (isClient || isHuman);
  const canForwardMessage = whatsappQrCode;
  const forwardOption = canForwardMessage
    ? [{ label: "Encaminhar mensagem", value: "forward" }]
    : [];
  const optionsToUse = isHistoricalMessage
    ? [
        ...forwardOption,
        ...(canReplyToMessage
          ? [{ label: "Responder mensagem", value: "reply" }]
          : [])
      ]
    : hasFiles
    ? [
        { label: "Apagar mensagem", value: "delete" },
        ...forwardOption,
        ...(canReplyToMessage
          ? [{ label: "Responder mensagem", value: "reply" }]
          : [])
      ]
    : isClient
    ? [
        { label: "Apagar mensagem", value: "delete" },
        ...forwardOption,
        ...(canReplyToMessage
          ? [{ label: "Responder mensagem", value: "reply" }]
          : [])
      ]
    : [
        { label: "Apagar mensagem", value: "delete" },
        ...(isForwardedMessage
          ? []
          : [{ label: "Editar mensagem", value: "edit" }]),
        ...forwardOption,
        ...(canReplyToMessage
          ? [{ label: "Responder mensagem", value: "reply" }]
          : [])
      ];

  const isMessageDeleted =
    textMessageDeleted ||
    renderedMessage === "Mensagem apagada" ||
    message === "Mensagem apagada";

  if (files) {
    messageContent = message;
  } else {
    message.split("\n").map((m, index) => {
      const content = m.split(" ").map((mm, mIndex) =>
        URL_REGEX.test(mm) ? (
          <Fragment key={mIndex}>
            <a
              href={
                mm.includes("https://") || mm.includes("http://")
                  ? mm
                  : `https://${mm}`
              }
              target="_blank"
              rel="noreferrer"
            >
              {mm}
            </a>{" "}
          </Fragment>
        ) : (
          <Fragment key={mIndex}>{mm} </Fragment>
        )
      );
      return <div key={index}>{content}</div>;
    });
  }

  const cancelDeleteMessage = () => {
    setShowModalDelete(false);
    dispatch(setModalEdit(true));
  };

  const handleDeleteMessage = async () => {
    try {
      await deleteMessages(id);
      setTextMessageDeleted(true);
      onMessageDeleted(id);
      toast?.current?.show({
        life: 3000,
        severity: "success",
        summary: "Sucesso",
        detail: "Mensagem apagada com sucesso!",
        sticky: false
      });
    } catch (error) {
      console.error(error);
      toast?.current?.show({
        life: 3000,
        severity: "error",
        summary: "Erro.",
        detail:
          "Erro ao tentar apagar a mensagem. Por favor, tente mais tarde.",
        sticky: false
      });
    }
    setDeletedMessage(true);
    setShowModalDelete(false);
  };

  const removeNomeAgent = (rawMessage: string, nameAgent?: string) => {
    const agent = (nameAgent ?? "").trim();
    if (!agent) return rawMessage;

    const msg = String(rawMessage ?? "")
      .replace(/\r\n/g, "\n")
      .replace(/^\uFEFF/, "");

    const escaped = agent.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`^\\s*${escaped}\\s*(?:[:\\-])?\\s*\\n+`, "i");

    return re.test(msg) ? msg.replace(re, "") : rawMessage;
  };

  useEffect(() => {
    if (!showModalEdit) return;
    if (!emoji) return;

    setEditedText((prev) => {
      const text = String(prev ?? "");
      const start = Math.max(0, Math.min(cursorPosition, text.length));

      const first = text.slice(0, start);
      const last = text.slice(start);
      return `${first}${emoji}${last}`;
    });
  }, [emoji]);

  const handleSaveEdit = async () => {
    try {
      const res = await editMessage({
        dialogId,
        messageId: id,
        editedMessage: editedText
      });

      const editedFromApi =
        (res as any)?.data?.editedMessage ??
        (res as any)?.editedMessage ??
        null;

      toast?.current?.show({
        life: 3000,
        severity: "success",
        summary: "Sucesso",
        detail: "Mensagem editada com sucesso!",
        sticky: false
      });

      setRenderedMessage(editedFromApi ?? editedText);
      setRenderedUpdatedAt(new Date());

      dispatch(setRefreshAllDialogs(true));
      setShowModalEdit(false);
      dispatch(setModalEdit(true));
    } catch (error: any) {
      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Não foi possível editar a mensagem.";

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  };

  useEffect(() => {
    setRenderedMessage(message);
    setRenderedUpdatedAt(updatedAt);
  }, [message, updatedAt]);

  const replyMessage = () => {
    dispatch(
      setReplyMessage({
        id,
        dialogId,
        message: renderedMessage,
        files,
        date: new Date(date).toISOString(),
        updatedAt: renderedUpdatedAt
          ? renderedUpdatedAt.toISOString()
          : undefined,
        reply,
        isHumanResponder,
        robotName,
        chatName,
        humanName: messageSenderName
      })
    );
  };

  type Kind = "text" | "image" | "audio" | "video" | "other";

  const parentKind: Kind = (() => {
    const mime = (quotedMessage?.mime ?? "").trim();
    if (mime.startsWith("image/")) return "image";
    if (mime.startsWith("audio/")) return "audio";
    if (mime.startsWith("video/")) return "video";
    if (mime) return "other";

    const text = (quotedMessage?.message ?? "").trim();
    if (text) return "text";

    return "text";
  })();

  const tryOpenDelete = () => {
    if (isHistoricalMessage) return;

    const canDelete = hasAnyPermission("message_remove") || user.admin;

    if (!canDelete) {
      toast?.current?.show({
        life: 4000,
        severity: "error",
        summary: "Sem permissão",
        detail: "O seu perfil não possui permissão para apagar mensagens.",
        sticky: false
      });
      return;
    }

    setShowModalDelete(true);
  };

  const tryOpenReply = () => {
    if (!canReplyToMessage) return;

    if (isMessageDeleted) return;

    replyMessage();
  };

  const tryOpenEdit = () => {
    if (isHistoricalMessage) return;

    if (isClient) return;
    if (isForwardedMessage) return;
    if (Array.isArray(files) && files.length > 0) return;

    const older15Min = Date.now() - new Date(date).getTime() > 15 * 60 * 1000;

    if (older15Min) {
      toast?.current?.show({
        life: 4000,
        severity: "error",
        summary: "Erro",
        detail: "Só é possível editar mensagens em até 15 minutos.",
        sticky: false
      });
      return;
    }

    setEditedText(removeNomeAgent(renderedMessage, user.nameAgent));
    setShowModalEdit(true);
    dispatch(setModalEdit(false));
  };

  const closeEditModal = () => {
    setShowModalEdit(false);
    dispatch(setModalEdit(true));
  };

  const tryOpenForward = () => {
    if (!canForwardMessage) return;
    if (isMessageDeleted) return;

    setForwardSearch("");
    setForwardContacts([]);
    setForwardContactsOffset(0);
    setForwardContactsHasMore(true);
    setSelectedForwardContacts([]);
    setShowModalForward(true);
  };

  useEffect(() => {
    if (!showModalForward) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowModalForward(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showModalForward]);

  useEffect(() => {
    if (!showModalForward) return;

    const loadForwardContacts = async () => {
      if (!forwardWorkspaces.length) {
        setForwardContacts([]);
        setForwardContactsOffset(0);
        setForwardContactsHasMore(false);
        return;
      }

      try {
        forwardContactsLoadingRef.current = true;
        setForwardContactsLoading(true);

        const data = await ListContactsOmnichannel({
          offset: 0,
          limit: FORWARD_CONTACTS_LIMIT,
          workspaces: forwardWorkspaces,
          description: debouncedForwardSearch
        });

        const result = data.result ?? [];
        const total = data.total ?? result.length;
        setForwardContacts(result);
        setForwardContactsOffset(result.length);
        setForwardContactsHasMore(result.length < total);
      } catch (error) {
        toast?.current?.show({
          life: 5000,
          severity: "error",
          summary: "Erro.",
          detail: "Erro ao listar os contatos."
        });
      } finally {
        forwardContactsLoadingRef.current = false;
        setForwardContactsLoading(false);
      }
    };

    loadForwardContacts();
  }, [showModalForward, debouncedForwardSearch, forwardWorkspaces]);

  const loadMoreForwardContacts = async () => {
    if (forwardContactsLoadingRef.current) return;
    if (!forwardContactsHasMore) return;
    if (!showModalForward) return;
    if (!forwardWorkspaces.length) return;

    try {
      forwardContactsLoadingRef.current = true;
      setForwardContactsLoading(true);

      const currentOffset = forwardContactsOffset;
      const data = await ListContactsOmnichannel({
        offset: currentOffset,
        limit: FORWARD_CONTACTS_LIMIT,
        workspaces: forwardWorkspaces,
        description: debouncedForwardSearch
      });

      const result = data.result ?? [];
      const total = data.total ?? currentOffset + result.length;
      setForwardContacts((prev) => [...prev, ...result]);
      setForwardContactsOffset(currentOffset + result.length);
      setForwardContactsHasMore(currentOffset + result.length < total);
    } catch (error) {
      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro.",
        detail: "Erro ao listar os contatos."
      });
    } finally {
      forwardContactsLoadingRef.current = false;
      setForwardContactsLoading(false);
    }
  };

  const handleForwardContactsScroll = (event: ReactUIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const reachedBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 16;

    if (reachedBottom) {
      loadMoreForwardContacts();
    }
  };

  const toggleForwardContact = (contactId: number) => {
    setSelectedForwardContacts((prev) =>
      prev.includes(contactId)
        ? prev.filter((selectedId) => selectedId !== contactId)
        : [...prev, contactId]
    );
  };

  const handleConfirmForward = () => {
    if (selectedForwardContacts.length === 0) return;

    const forwardText =
      renderedMessage?.trim() ||
      message?.trim() ||
      (hasFiles ? "Mensagem encaminhada" : "");

    onForwardMessage(id, forwardText, selectedForwardContacts, dialogId);
    setShowModalForward(false);
    setSelectedForwardContacts([]);
    setForwardSearch("");
  };

  const handleMessageAction = (value: string) => {
    if (value === "delete") {
      tryOpenDelete();
    } else if (value === "edit") {
      tryOpenEdit();
    } else if (value === "reply") {
      tryOpenReply();
    } else if (value === "forward") {
      tryOpenForward();
    }
  };

  useEffect(() => {
    if (!showModalEdit) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeEditModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    requestAnimationFrame(() => {
      const el = editedTextRef.current;
      if (!el) return;

      el.focus();

      const len = el.value?.length ?? 0;
      el.setSelectionRange(len, len);
      setCursorPosition(len);
    });

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showModalEdit]);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: ${reply ? "flex-end" : "flex-start"};
        margin-bottom: 10px;
        margin-top: 10px;
        width: 100%;
      `}
    >
      <div
        css={css`
          align-items: center;
          display: flex;
          gap: 10px;
        `}
      >
        <div
          className={messageTooltipClass}
          data-pr-position="bottom"
          onDoubleClick={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest("a")) return;
            if (target.closest("img")) return;
            if (target.closest(".custom-dropdown")) return;

            tryOpenReply();
          }}
          css={css`
            align-items: ${reply ? "flex-end" : "flex-start"};
            background: ${reply
              ? "#f0eff5"
              : "linear-gradient(90deg, #046af3 0%, #4694fe 100%)"};
            border-radius: ${reply
              ? "16px 16px 16px 0px"
              : "16px 16px 0px 16px"};
            ${reply ? "" : "color: #ffffff;"}
            display: flex;
            max-width: 40vw;
            flex-direction: column;
            gap: 5px;
            padding: 10px;
            a {
              ${reply ? "" : "color: #ffffff;"};
            }
            display: flex;

            @media screen and (max-width: 1024px) {
              max-width: 60vw;
            }

            @media screen and (max-width: 599px) {
              max-width: 70vw;
            }
          `}
        >
          <Tooltip target={`.${messageTooltipClass}`}>
            {tooltipSenderName}
          </Tooltip>
          {isTemporaryFailed && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%"
              }}
            >
              <div
                onClick={isTemporaryFailed ? onRetry : undefined}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "end",
                  gap: "6px",
                  cursor: isTemporaryFailed ? "pointer" : "default",
                  height: "20px"
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    height: "20px",
                    lineHeight: "20px",
                    fontSize: "11px",
                    fontWeight: 500,
                    color: reply ? "#6b7280" : "#dbeafe"
                  }}
                >
                  Tentar novamente
                </span>
                {isTemporaryFailed && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "20px",
                      border: "none",
                      color: "#0f62fe",
                      flexShrink: 0
                    }}
                  >
                    <span
                      className="pi pi-refresh"
                      style={{
                        color: "#495057",
                        lineHeight: 1
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ width: "100%" }}>
            {Array.isArray(files) &&
              files.length > 0 &&
              whatsappQrCode &&
              !sendState &&
              !textMessageDeleted && (
                <DropdownDeleteMessage>
                  {message !== "Mensagem apagada" &&
                    optionsToUse.length > 0 && (
                      <Dropdown
                        onChange={(e) => {
                          handleMessageAction(e.value);
                        }}
                        options={optionsToUse}
                        style={{
                          background: reply
                            ? "#f0eff5"
                            : "linear-gradient(90deg, #046af3 0%, #4694fe 100%)",
                          border: "none",
                          height: "15px",
                          marginTop: "-5px",
                          width: "100%"
                        }}
                        placeholder=""
                        className="custom-dropdown"
                      />
                    )}
                </DropdownDeleteMessage>
              )}
          </div>

          {Array.isArray(files) && files.length > 0 && textMessageDeleted ? (
            <div
              style={{
                wordWrap: "break-word",
                whiteSpace: "normal",
                overflow: "break-work",
                width: "100%"
              }}
            >
              Mensagem apagada
            </div>
          ) : (
            files?.map((file, idx) => {
              if (file.mime.startsWith("image")) {
                return <FileImagePreview key={idx} file={file} />;
              }
              if (file.mime.startsWith("audio")) {
                return (
                  <AudioPlayer
                    url={file.url}
                    key={idx}
                    played={isPlayedMessageStatus}
                  />
                );
              }
              if (file.mime.startsWith("video")) {
                return <VideoPreview videoURL={file.url} key={idx} />;
              }
              return (
                <FileLinkURL
                  filename={file.fileName}
                  message={message}
                  key={idx}
                  file={file}
                />
              );
            })
          )}

          <div
            style={{ display: "flex", flexDirection: "column", width: "100%" }}
          >
            {!textMessageDeleted &&
              whatsappQrCode &&
              Array.isArray(files ?? []) &&
              (files ?? []).length < 1 &&
              !isTemporaryFailed && (
                <DropdownDeleteMessage>
                  {message !== "Mensagem apagada" &&
                    !sendState &&
                    optionsToUse.length > 0 && (
                      <Dropdown
                        onChange={(e) => {
                          handleMessageAction(e.value);
                        }}
                        options={optionsToUse}
                        style={{
                          background: reply
                            ? "#f0eff5"
                            : "linear-gradient(90deg, #046af3 0%, #4694fe 100%)",
                          border: "none",
                          height: "15px",
                          marginTop: "-5px",
                          width: "100%"
                        }}
                        placeholder=""
                      />
                    )}
                </DropdownDeleteMessage>
              )}

            {quotedParentId > 0 && !isMessageDeleted && (
              <ParentId
                $variant={reply ? "agent" : "client"}
                $kind={parentKind}
              >
                {(() => {
                  if (deletedParentIds?.has(quotedParentId)) {
                    return "Mensagem apagada";
                  }

                  const quotedText = (quotedMessage?.message ?? "").trim();
                  const quotedMime = (quotedMessage?.mime ?? "").trim();

                  if (quotedMime) {
                    if (quotedMime.startsWith("image/")) {
                      return (
                        <FileImagePreview
                          file={{ url: quotedText, mime: quotedMime } as any}
                        />
                      );
                    }

                    if (quotedMime.startsWith("audio/")) {
                      return quotedText ? (
                        <AudioPlayer url={quotedText} />
                      ) : (
                        <div>Áudio citado não carregado</div>
                      );
                    }

                    if (quotedMime.startsWith("video/")) {
                      return quotedText ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            maxWidth: "400px"
                          }}
                        >
                          <VideoPreview videoURL={quotedText} />
                        </div>
                      ) : (
                        <div>Vídeo citado não carregado</div>
                      );
                    }

                    return (
                      <FileLinkURL
                        filename="Arquivo"
                        message={quotedText}
                        file={{ url: quotedText } as any}
                      />
                    );
                  }

                  if (quotedText) return quotedText;

                  return "Mensagem citada não carregada";
                })()}
              </ParentId>
            )}

            <div
              style={{
                wordWrap: "break-word",
                whiteSpace: "pre-wrap",
                overflow: "break-work",
                width: "100%"
              }}
            >
              {(() => {
                const text =
                  Array.isArray(files) && files.length > 0 && textMessageDeleted
                    ? null
                    : deletedMessage
                    ? textMessageDeleted
                      ? "Mensagem apagada"
                      : message
                    : textMessageDeleted &&
                      Array.isArray(files) &&
                      files.length === 0
                    ? "Mensagem apagada"
                    : renderedMessage ?? "";

                if (!text) return null;
                if (text === "Mensagem apagada") return text;

                const t = String(text).trim();
                const isFile = /^(https?:\/\/|www\.)/i.test(t);
                const href = t.startsWith("http") ? t : `https://${t}`;

                return isFile ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: "underline" }}
                  >
                    {text}
                  </a>
                ) : (
                  text
                );
              })()}
            </div>
          </div>

          <div
            css={css`
              font-size: 12px;
              display: flex;
              flexdirection: row;
              align-items: center;
              gap: 10px;
            `}
          >
            {isForwardedMessage && <div>Mensagem encaminhada</div>}
            {renderedUpdatedAt && <div>Editada</div>}

            {getMaskedDate(renderedUpdatedAt ?? date)}

            {(isPendingMessageStatus ||
              isServerAckMessageStatus ||
              isDeliveryAckMessageStatus ||
              isReadMessageStatus ||
              isPlayedMessageStatus ||
              isErrorMessageStatus) && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: 14,
                  gap: "4px",
                  lineHeight: 0
                }}
              >
                {isPendingMessageStatus ? (
                  <i className="pi pi-clock" title="Enviando" />
                ) : isServerAckMessageStatus ? (
                  <div
                    title="Enviada"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      height: 14,
                      lineHeight: 0
                    }}
                  >
                    <ColorSvg src={WhatsAppCheck} color="#7B89A3" size={14} />
                  </div>
                ) : isDeliveryAckMessageStatus ||
                  isReadMessageStatus ||
                  isPlayedMessageStatus ? (
                  <div
                    title={
                      isPlayedMessageStatus
                        ? "Reproduzida"
                        : isReadMessageStatus
                        ? "Lida"
                        : "Entregue"
                    }
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      width: 20,
                      height: 14,
                      lineHeight: 0
                    }}
                  >
                    {isDeliveryAckMessageStatus ? (
                      <>
                        <span
                          style={{
                            position: "absolute",
                            display: "flex",
                            alignItems: "center",
                            left: 0,
                            width: 14,
                            height: 14,
                            lineHeight: 0
                          }}
                        >
                          <ColorSvg
                            src={WhatsAppCheck}
                            color="#7B89A3"
                            size={14}
                          />
                        </span>
                        <span
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            display: "flex",
                            alignItems: "center",
                            left: 6,
                            width: 14,
                            height: 14,
                            lineHeight: 0
                          }}
                        >
                          <ColorSvg
                            src={WhatsAppCheck}
                            color="#7B89A3"
                            size={14}
                          />
                        </span>
                      </>
                    ) : (
                      <>
                        <img
                          src={WhatsAppCheck}
                          alt={isPlayedMessageStatus ? "Reproduzida" : "Lida"}
                          style={{
                            position: "absolute",
                            display: "block",
                            left: 0,
                            width: 14,
                            height: 14
                          }}
                        />
                        <img
                          src={WhatsAppCheck}
                          alt=""
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            display: "block",
                            left: 6,
                            width: 14,
                            height: 14
                          }}
                        />
                      </>
                    )}
                  </div>
                ) : (
                  <div
                    title="Falha ao enviar"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      height: 14,
                      lineHeight: 0
                    }}
                  >
                    <img
                      src={CloseXRed}
                      alt="Falha ao enviar"
                      title="Falha ao enviar"
                      style={{ display: "block", width: 12, height: 12 }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Dialog
        visible={showModalDelete}
        onHide={cancelDeleteMessage}
        header="Confirmar exclusão"
        draggable={false}
        footer={
          <div style={{ display: "flex", justifyContent: "end" }}>
            <Button
              label="Apagar"
              className="p-button-danger ml-2 mr-2"
              onClick={handleDeleteMessage}
            />
            <Button
              label="Cancelar"
              className="ml-2 mr-2"
              onClick={cancelDeleteMessage}
            />
          </div>
        }
      >
        <p>Tem certeza de que deseja apagar esta mensagem?</p>
      </Dialog>

      {showModalForward && (
        <ForwardMessageModal $mainColor={user.plataform.mainColor || "#2624CD"}>
          <button
            type="button"
            className="closeButton"
            aria-label="Fechar"
            onClick={() => setShowModalForward(false)}
          >
            <span className="pi pi-times" />
          </button>

          <div className="header">
            <div className="botIcon">
              <img
                src={user.plataform.urlLogo ? user.plataform.urlLogo : spaik}
              />
            </div>
            <div className="title">Encaminhar mensagem</div>
          </div>

          <div className="search">
            <span className="pi pi-search" />
            <input
              value={forwardSearch}
              onChange={(e) => setForwardSearch(e.target.value)}
              placeholder="Pesquisar por nome ou telefone"
            />
          </div>

          <div className="contacts" onScroll={handleForwardContactsScroll}>
            {forwardContactsLoading && !forwardContacts.length ? (
              <div className="empty">Carregando contatos...</div>
            ) : forwardContacts.length ? (
              <>
                {forwardContacts.map((contact) => {
                  const checked = selectedForwardContacts.includes(contact.id);
                  const contactName = contact.name || "Sem nome";

                  return (
                    <label className="contact" key={contact.id}>
                      <Checkbox
                        inputId={`forward-contact-${contact.id}`}
                        checked={checked}
                        onChange={() => toggleForwardContact(contact.id)}
                      />
                      {contact.avatarUrl ? (
                        <img
                          src={contact.avatarUrl}
                          alt={contactName}
                          css={css`
                            height: 43px;
                            width: 43px;
                            border-radius: 50%;
                            object-fit: cover;
                          `}
                        />
                      ) : (
                        <Avatar name={contactName} />
                      )}
                      <span className="contactInfo">
                        <span className="name">{contactName}</span>
                        <span className="phone">{contact.phone || ""}</span>
                      </span>
                    </label>
                  );
                })}
                {forwardContactsLoading && (
                  <div className="empty">Carregando contatos...</div>
                )}
              </>
            ) : (
              <div className="empty">Nenhum contato encontrado.</div>
            )}
          </div>

          <Button
            label="Encaminhar"
            disabled={selectedForwardContacts.length === 0}
            className="forwardButton"
            onClick={handleConfirmForward}
          />
        </ForwardMessageModal>
      )}

      {showModalEdit && (
        <ModalEditMessage
          isEmojiOpen={emojiModalOpen}
          $mainColor={user.plataform.mainColor || "#2624CD"}
        >
          <div className="messages">
            <div className="title">Editar mensagem</div>
            <div className="originalMessage">
              <div className="originalTitle">Mensagem original</div>
              <div className="originalText">{renderedMessage}</div>
            </div>

            <div className="editedMessage">
              <div className="newMessage">Nova mensagem</div>

              <textarea
                ref={editedTextRef}
                className="editedText"
                value={editedText}
                placeholder="Digite a nova mensagem..."
                onChange={(e) => setEditedText(e.target.value)}
                onKeyUp={(e) =>
                  setCursorPosition(e.currentTarget.selectionStart)
                }
                onClick={(e) =>
                  setCursorPosition(e.currentTarget.selectionStart)
                }
                onSelect={(e) =>
                  setCursorPosition(e.currentTarget.selectionStart)
                }
              />
              <ImgIcon
                className="tooltip-Smile"
                onClick={() => {
                  dispatch(setEmojiModalOpen(true));
                }}
              >
                <img src={Smile} alt="Icon-Smiles" />
              </ImgIcon>
              <Tooltip target=".tooltip-Smile">Enviar Emoji</Tooltip>
            </div>
          </div>

          <div className="buttons">
            <Button
              label="CANCELAR"
              className="cancel"
              onClick={closeEditModal}
            />
            <Button
              label="CONFIRMAR"
              className="confirm"
              onClick={handleSaveEdit}
            />
          </div>
          <EmojiModal heightEmojiPicker="330px" />
        </ModalEditMessage>
      )}
    </div>
  );
};

export interface MessageButtonProps {
  label: string;
}

const MessageButton = ({ label }: MessageButtonProps) => (
  <div
    css={css`
      background-color: #ffffff;
      border: 1px solid #d2d2d2;
      border-radius: 8px;
      padding: 8px;
      max-width: 90vw;
      overflow-wrap: break-word;
      white-space: normal;
    `}
  >
    {label}
  </div>
);

export default MessageButton;
